import {
  IClientAttributeModel,
  IClientAssociationModel,
  CondOperatorType,
} from '../../../../../Types/ClientModelTypes';
import {
  IUrvalCondition,
  IStringConditionObject,
  INumericConditionObject,
  TypeTreeType,
  UIUrvalConditionObjectType,
  IExtendedConditionObject,
  ITypeTreeConditionObject,
  NonExtendedConditionObjectType,
  IUrvalBooleanCondition,
  IUIPage,
} from '../../../../../Types/UrvalTypes';
import shortid from 'shortid';
import {
  isStringType,
  isNumericType,
} from '../../SavedUrval/Utils/convertSavedUrval';
import {
  getTypeTree,
  getAttribute,
} from '../../../../../Model/avenyModelUtils';
import { getNewPage } from '../../../Utils/urvalUtils';
import { AvenyCollectionMap } from '../../../../../Model/ModelContext/ModelContext';
import {
  TEDSelectionExtendedOperator,
  TEDSelectionOperator,
} from '../../../../../globalQueryTypes';
import Logger from '../../../../../Utils/Logger';
import { times } from 'ramda';

const getDefaultOperator = (
  attribute: IClientAttributeModel
): CondOperatorType => {
  let op = attribute.DefaultCondOperator;

  if (op == null) {
    op = attribute.CondOperators?.[0];

    if (op == null) {
      Logger.error(
        Error(
          `Attribute ${attribute.AttributeName} has no default condition operator and no available condition operators`
        )
      );
      op = TEDSelectionOperator.starts;
    }
  }

  return op;
};

export const getDefaultStringCondition = (
  attribute: IClientAttributeModel,
  nrValues: number = 1
): IUrvalCondition => {
  const op = getDefaultOperator(attribute);

  return {
    id: shortid.generate(),
    op,
    values: times(
      () => ({
        id: shortid.generate(),
        value: '',
        toValue: '',
      }),
      nrValues
    ),
  };
};

export const getDefaultNumericCondition = (
  attribute: IClientAttributeModel
): IUrvalCondition => {
  const op = getDefaultOperator(attribute);

  return {
    id: shortid.generate(),
    op,
    values: [{ id: shortid.generate(), value: undefined, toValue: undefined }],
  };
};

const getDefaultStringConditionObject = (
  id: string,
  attribute: IClientAttributeModel,
  modelPath?: IClientAssociationModel[]
): IStringConditionObject => ({
  id,
  type: 'String',
  attribute,
  modelPath,
  condition: [getDefaultStringCondition(attribute)],
});

const getDefaultNumericConditionObject = (
  id: string,
  attribute: IClientAttributeModel,
  modelPath?: IClientAssociationModel[]
): INumericConditionObject => ({
  id,
  type: 'Numeric',
  attribute,
  modelPath,
  condition: [getDefaultNumericCondition(attribute)],
});

export const createConditionPath = (
  name: string,
  modelPath?: IClientAssociationModel[]
): string => {
  if (modelPath && modelPath.length > 0) {
    const associationPath = modelPath
      .map(assoc => assoc.AssociationName)
      .join('.');
    return `${associationPath}.${name}`;
  } else {
    return name;
  }
};

export const createConditionID = (
  name: string,
  modelPath?: IClientAssociationModel[]
): string => `${createConditionPath(name, modelPath)} - ${shortid.generate()}`;

export const getDefaultConditionObject = (
  attribute: IClientAttributeModel,
  modelPath?: IClientAssociationModel[],
  typeTree?: TypeTreeType | null
): UIUrvalConditionObjectType => {
  const type = attribute.DataType;

  const id = createConditionID(attribute.AttributeName, modelPath);

  if (attribute.InterfaceType === 'ifFilter') {
    return {
      id,
      type: 'Filter',
      attribute,
      modelPath,
      condition: false,
    };
  } else if (type === 'Boolean') {
    return {
      id,
      type: 'Boolean',
      attribute,
      modelPath,
      condition: { no: false, yes: false },
    };
  } else if (attribute.InterfaceType === 'ifTypeTree' && typeTree) {
    return {
      id,
      type: 'TypeTree',
      attribute,
      modelPath,
      typeTree,
      condition: [],
      implicitCondition: [],
    };
  } else if (type === 'Enum') {
    return { id, type: 'Checklist', attribute, modelPath, condition: [] };
  } else if (isStringType(type)) {
    return getDefaultStringConditionObject(id, attribute, modelPath);
  } else if (isNumericType(type)) {
    return getDefaultNumericConditionObject(id, attribute, modelPath);
  } else {
    // Should never get here when all types are handled
    Logger.error(
      `No default condition for interface type: ${attribute.InterfaceType} provided`
    );
    return getDefaultStringConditionObject(id, attribute, modelPath);
  }
};

export const getDefaultNonExtendedConditionObject = (
  attribute: IClientAttributeModel,
  modelPath?: IClientAssociationModel[],
  typeTree?: TypeTreeType | null
): NonExtendedConditionObjectType => {
  const type = attribute.DataType;

  const id = createConditionID(attribute.AttributeName, modelPath);

  if (attribute.InterfaceType === 'ifFilter') {
    return {
      id,
      type: 'Filter',
      attribute,
      modelPath,
      condition: false,
    };
  } else if (type === 'Boolean') {
    return {
      id,
      type: 'Boolean',
      attribute,
      modelPath,
      condition: { no: false, yes: false },
    };
  } else if (attribute.InterfaceType === 'ifTypeTree' && typeTree) {
    return {
      id,
      type: 'TypeTree',
      attribute,
      modelPath,
      typeTree,
      condition: [],
      implicitCondition: [],
    };
  } else if (type === 'Enum') {
    return { id, type: 'Checklist', attribute, modelPath, condition: [] };
  } else if (isStringType(type)) {
    return getDefaultStringConditionObject(id, attribute, modelPath);
  } else if (isNumericType(type)) {
    return getDefaultNumericConditionObject(id, attribute, modelPath);
  } else {
    // Should never get here when all types are handled
    Logger.error(
      `No default condition for interface type: ${attribute.InterfaceType} provided`
    );
    return getDefaultStringConditionObject(id, attribute, modelPath);
  }
};

export const getDefaultExtendedConditionObject = (
  collectionMap: AvenyCollectionMap,
  association: IClientAssociationModel,
  modelPath?: IClientAssociationModel[]
): IExtendedConditionObject => {
  const id = createConditionID(association.AssociationName, modelPath);

  return {
    id: id,
    type: 'Extended',
    association: association,
    modelPath: modelPath,
    condition: [
      {
        id: shortid.generate(),
        in: [
          getNewPage(
            collectionMap,
            association.AssociatedCollectionName,
            association.AssociatedComponentName
          ),
        ],
        op: TEDSelectionExtendedOperator.exists,
      },
    ],
  };
};

export const getTypeTreeConditionObject = (
  collectionName: string,
  componentName: string,
  collectionMap: AvenyCollectionMap
): ITypeTreeConditionObject | undefined => {
  let typeTreeObject: ITypeTreeConditionObject | undefined;

  const typeTree = getTypeTree(collectionMap, collectionName, componentName);

  if (typeTree.ChildTypes.length > 0) {
    let typeAttribute = getAttribute(
      collectionName,
      componentName,
      '_typ',
      collectionMap
    );

    if (typeAttribute != null) {
      typeTreeObject = {
        id: typeAttribute.AttributeName,
        type: 'TypeTree',
        attribute: typeAttribute,
        modelPath: [],
        typeTree: typeTree,
        condition: [],
        implicitCondition: [],
      };
    } else {
      return undefined;
    }
  }

  return typeTreeObject;
};

export const createAssocLabel = (
  modelPath?: IClientAssociationModel[]
): string => {
  if (!modelPath || modelPath.length === 0) {
    return '';
  }
  const viewPath = modelPath.filter(assoc => !assoc.Integrated);
  if (viewPath.length === 0) {
    return '';
  }
  const assocLabels = viewPath.map(assoc => assoc.DisplayLabel);
  if (assocLabels.length > 1) {
    return assocLabels.join(', ');
  } else {
    return assocLabels[0];
  }
};

/**
 * Current design element sizes.
 */
const containerMargin = 24;
const headerLabelHeight = 48;
const conditionHeight = 64;
const doubleConditionHeight = 126;
const checkboxConditionHeight = 42;
const checkboxConditionMinimumHeight = 48;
const helperTextHeight = 15;
const helperTextDoubleHeight = 32;
const dividerHeight = 1;

export const getUrvalPageHeights = (
  pages?: IUIPage[],
  screenSizeSmall?: boolean,
  screenSizeMedium?: boolean,
  inner?: boolean
): number[] => {
  if (!pages) {
    return [];
  }
  return pages.map(x =>
    x.conditionObjects.reduce(
      (a, b) => {
        if (b.type === 'Extended') {
          return (
            a +
            b.condition.reduce((c, d) => {
              return (
                c +
                getUrvalPageHeights(
                  d.in,
                  screenSizeSmall,
                  screenSizeMedium,
                  true
                ).reduce((e, f) => e + f, 0)
              );
            }, conditionHeight + dividerHeight * 2)
          );
        } else if (b.type === 'Boolean') {
          return a + containerMargin + checkboxConditionMinimumHeight;
        } else if (b.type === 'Checklist') {
          const nrRows = screenSizeSmall
            ? (b.attribute.EnumValues?.size ?? 0) + 1
            : Math.ceil(((b.attribute.EnumValues?.size ?? 0) + 1) / 2);
          return (
            a +
            containerMargin +
            Math.max(
              nrRows * checkboxConditionHeight,
              checkboxConditionMinimumHeight
            )
          );
        } else {
          const isKontodel = b.attribute.AttributeName.startsWith('KONTODEL_');
          return (
            a + getConditionHeight(b.condition, isKontodel, screenSizeMedium)
          );
        }
      },
      inner ? 0 : headerLabelHeight + containerMargin * 2
    )
  );
};

const getConditionHeight = (
  condition: IUrvalBooleanCondition | boolean | IUrvalCondition[] | string[],
  isKontodel: boolean,
  screenSizeMedium?: boolean
) => {
  if (!Array.isArray(condition)) {
    return conditionHeight;
  }
  if (condition.length === 0) {
    return 0;
  }
  if (typeof condition[0] === 'string') {
    return condition.length * conditionHeight;
  }
  return (condition as IUrvalCondition[]).reduce((g, h) => {
    return (
      g +
      h.values.length *
        urvalConditionTypeHeight(h, isKontodel, screenSizeMedium)
    );
  }, 0);
};

const urvalConditionTypeHeight = (
  condition: IUrvalCondition,
  isKontodel: boolean,
  screenSizeMedium?: boolean
): number => {
  const singleHeight = isKontodel
    ? conditionHeight + helperTextHeight
    : conditionHeight;
  const doubleHeight = isKontodel
    ? doubleConditionHeight + helperTextDoubleHeight
    : doubleConditionHeight;
  switch (condition.op) {
    case 'interval':
      return screenSizeMedium ? doubleHeight : singleHeight;

    case 'notinterval':
      return screenSizeMedium ? doubleHeight : singleHeight;

    default:
      return singleHeight;
  }
};
